import { useState, useEffect, useCallback, useRef } from 'react'
import type { ScoringStep } from '../engine/types'

export type ScoringAnimationPhase =
  | 'idle'
  | 'cards_spread'
  | 'hand_type_reveal'
  | 'card_scoring'
  | 'held_card_scoring'
  | 'joker_scoring'
  | 'final_calculation'
  | 'cards_collect'
  | 'done'

export type FloatingText = {
  id: string
  text: string
  color: string
  /** 关联卡ID (card_score 时) 或 Joker ID */
  targetId?: string
}

export type ScoringAnimationState = {
  phase: ScoringAnimationPhase
  currentStepIndex: number
  activeCardId: string | null
  activeJokerId: string | null
  displayChips: number
  displayMult: number
  floatingTexts: FloatingText[]
  finalScore: number | null
  handTypeCN: string
  handTypeLevel: number
  skip: () => void
}

// Timing constants (ms)
const CARDS_SPREAD_DURATION = 1200
const HAND_TYPE_REVEAL_DURATION = 1200
const CARD_SCORING_INTERVAL = 800
const HELD_CARD_INTERVAL = 800
const JOKER_SCORING_INTERVAL = 1100
const FINAL_CALC_DURATION = 1600
const CARDS_COLLECT_DURATION = 700

let floatIdCounter = 0

export function useScoringAnimation(
  steps: ScoringStep[],
  isActive: boolean,
  onComplete: () => void,
): ScoringAnimationState {
  const [phase, setPhase] = useState<ScoringAnimationPhase>('idle')
  const [currentStepIndex, setCurrentStepIndex] = useState(0)
  const [activeCardId, setActiveCardId] = useState<string | null>(null)
  const [activeJokerId, setActiveJokerId] = useState<string | null>(null)
  const [displayChips, setDisplayChips] = useState(0)
  const [displayMult, setDisplayMult] = useState(0)
  const [floatingTexts, setFloatingTexts] = useState<FloatingText[]>([])
  const [finalScore, setFinalScore] = useState<number | null>(null)
  const [handTypeCN, setHandTypeCN] = useState('')
  const [handTypeLevel, setHandTypeLevel] = useState(1)

  const skippedRef = useRef(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const onCompleteRef = useRef(onComplete)
  onCompleteRef.current = onComplete

  const addFloat = useCallback((text: string, color: string, targetId?: string) => {
    const id = `float_${++floatIdCounter}`
    setFloatingTexts(prev => [...prev, { id, text, color, targetId }])
    // Auto-remove after animation
    setTimeout(() => {
      setFloatingTexts(prev => prev.filter(f => f.id !== id))
    }, 1200)
  }, [])

  const skipAll = useCallback(() => {
    if (skippedRef.current) return
    skippedRef.current = true
    if (timerRef.current) clearTimeout(timerRef.current)

    // Jump to final state
    const finalStep = steps.find(s => s.type === 'final_score')
    if (finalStep && finalStep.type === 'final_score') {
      setDisplayChips(finalStep.totalChips)
      setDisplayMult(finalStep.totalMult)
      setFinalScore(finalStep.handScore)
    }
    setActiveCardId(null)
    setActiveJokerId(null)
    setFloatingTexts([])
    setPhase('done')
    onCompleteRef.current()
  }, [steps])

  // Main animation sequence
  useEffect(() => {
    if (!isActive || steps.length === 0) {
      setPhase('idle')
      return
    }

    skippedRef.current = false
    setCurrentStepIndex(0)
    setActiveCardId(null)
    setActiveJokerId(null)
    setDisplayChips(0)
    setDisplayMult(0)
    setFloatingTexts([])
    setFinalScore(null)

    // Categorize steps
    const handTypeBase = steps.find(s => s.type === 'hand_type_base')
    const cardScores = steps.filter(s => s.type === 'card_score')
    const heldCards = steps.filter(s => s.type === 'held_card')
    const jokerEffects = steps.filter(s => s.type === 'joker_effect')
    const finalStep = steps.find(s => s.type === 'final_score')

    let delay = 0

    // Phase 1: Cards spread
    setPhase('cards_spread')
    delay += CARDS_SPREAD_DURATION

    // Phase 2: Hand type reveal
    timerRef.current = setTimeout(() => {
      if (skippedRef.current) return
      setPhase('hand_type_reveal')
      if (handTypeBase && handTypeBase.type === 'hand_type_base') {
        setHandTypeCN(handTypeBase.handTypeCN)
        setHandTypeLevel(handTypeBase.level)
        setDisplayChips(handTypeBase.baseChips)
        setDisplayMult(handTypeBase.baseMult)
      }
    }, delay)
    delay += HAND_TYPE_REVEAL_DURATION

    // Phase 3: Card scoring (one by one)
    if (cardScores.length > 0) {
      timerRef.current = setTimeout(() => {
        if (skippedRef.current) return
        setPhase('card_scoring')
      }, delay)

      cardScores.forEach((step, i) => {
        const stepDelay = delay + i * CARD_SCORING_INTERVAL
        setTimeout(() => {
          if (skippedRef.current) return
          if (step.type === 'card_score') {
            setCurrentStepIndex(steps.indexOf(step))
            setActiveCardId(step.cardId)
            setDisplayChips(step.runningChips)
            setDisplayMult(step.runningMult)

            const chipAdd = step.chipValue + step.bonusChips
            if (chipAdd > 0) addFloat(`+${chipAdd}`, '#7CAFC2', step.cardId)
            if (step.bonusMult > 0) addFloat(`+${step.bonusMult}`, '#D4849A', step.cardId)
            if (step.enhancementMultiplier > 1) addFloat(`×${step.enhancementMultiplier}`, '#C9A96E', step.cardId)
          }
        }, stepDelay)
      })
      delay += cardScores.length * CARD_SCORING_INTERVAL
    }

    // Phase 3.5: Held card scoring (steel)
    if (heldCards.length > 0) {
      setTimeout(() => {
        if (skippedRef.current) return
        setPhase('held_card_scoring')
        setActiveCardId(null)
      }, delay)

      heldCards.forEach((step, i) => {
        const stepDelay = delay + i * HELD_CARD_INTERVAL
        setTimeout(() => {
          if (skippedRef.current) return
          if (step.type === 'held_card') {
            setActiveCardId(step.cardId)
            setDisplayMult(step.runningMult)
            addFloat(`×${step.multiplier}`, '#C9A96E', step.cardId)
          }
        }, stepDelay)
      })
      delay += heldCards.length * HELD_CARD_INTERVAL
    }

    // Phase 4: Joker scoring
    if (jokerEffects.length > 0) {
      setTimeout(() => {
        if (skippedRef.current) return
        setPhase('joker_scoring')
        setActiveCardId(null)
      }, delay)

      jokerEffects.forEach((step, i) => {
        const stepDelay = delay + i * JOKER_SCORING_INTERVAL
        setTimeout(() => {
          if (skippedRef.current) return
          if (step.type === 'joker_effect') {
            setCurrentStepIndex(steps.indexOf(step))
            setActiveJokerId(step.jokerId)
            setDisplayChips(step.runningChips)
            setDisplayMult(step.runningMult)

            const prefix = step.effectType === 'mult_mult' ? '×' : '+'
            const color = step.effectType === 'add_chips' ? '#7CAFC2'
              : step.effectType === 'add_mult' ? '#D4849A'
              : '#C9A96E'
            addFloat(`${prefix}${step.value}`, color, step.jokerId)
          }
        }, stepDelay)
      })
      delay += jokerEffects.length * JOKER_SCORING_INTERVAL
    }

    // Phase 5: Final calculation
    setTimeout(() => {
      if (skippedRef.current) return
      setPhase('final_calculation')
      setActiveCardId(null)
      setActiveJokerId(null)
      if (finalStep && finalStep.type === 'final_score') {
        setDisplayChips(finalStep.totalChips)
        setDisplayMult(finalStep.totalMult)
        setFinalScore(finalStep.handScore)
      }
    }, delay)
    delay += FINAL_CALC_DURATION

    // Phase 6: Cards collect (fly to score panel)
    setTimeout(() => {
      if (skippedRef.current) return
      setPhase('cards_collect')
    }, delay)
    delay += CARDS_COLLECT_DURATION

    // Done
    setTimeout(() => {
      if (skippedRef.current) return
      setPhase('done')
      onCompleteRef.current()
    }, delay)

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, steps])

  return {
    phase,
    currentStepIndex,
    activeCardId,
    activeJokerId,
    displayChips,
    displayMult,
    floatingTexts,
    finalScore,
    handTypeCN,
    handTypeLevel,
    skip: skipAll,
  }
}
